/**
 * Signage media uploader.
 *
 * Strategy:
 *   • files <= CHUNK_THRESHOLD_BYTES use a single XHR multipart POST to
 *     /media/upload, with real upload-progress events.
 *   • files > CHUNK_THRESHOLD_BYTES go through the chunked strategy
 *     (see `uploadChunked` stub below). The backend endpoints for that
 *     path are not yet implemented — the strategy is wired so the
 *     callers don't have to change when the backend adds them.
 *
 * XHR is used (not fetch) because fetch() does not expose upload
 * progress events. The RTK Query base query already forces
 * Content-Type: application/json which would break the multipart
 * boundary anyway, so this helper bypasses it.
 */

export type UploadStatus =
  | "queued"
  | "uploading"
  | "processing"
  | "done"
  | "error"
  | "cancelled";

export interface UploadOptions {
  onProgress?: (percent: number) => void;
  onStatus?: (status: UploadStatus) => void;
  signal?: AbortSignal;
  /**
   * Tenant the uploaded media belongs to. REQUIRED when the caller is a
   * super_admin (their own user row has no company). Tenant users may
   * omit it — the backend infers from Auth::user()->company_id.
   */
  companyId?: number;
}

export interface UploadResult {
  status: boolean;
  message?: string;
  data?: any;
}

/** 100 MB — anything bigger goes through the chunked path (future). */
export const CHUNK_THRESHOLD_BYTES = 100 * 1024 * 1024;

/** 500 MB — must match backend UploadMediaRequest::MAX_UPLOAD_KB. */
export const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;

/* ------------------------------------------------------------------------ */
/* Public API                                                                  */
/* ------------------------------------------------------------------------ */

export const uploadMedia = (
  file: File,
  opts: UploadOptions = {}
): Promise<UploadResult> => {
  if (file.size > MAX_UPLOAD_BYTES) {
    return Promise.reject(
      new Error(
        `File is too large (${formatBytes(file.size)}). Maximum allowed size is 500 MB.`
      )
    );
  }

  if (file.size > CHUNK_THRESHOLD_BYTES) {
    return uploadChunked(file, opts);
  }
  return uploadSingleShot(file, opts);
};

export const formatBytes = (b: number): string => {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

/* ------------------------------------------------------------------------ */
/* Single-shot multipart POST                                                  */
/* ------------------------------------------------------------------------ */

const uploadSingleShot = (
  file: File,
  opts: UploadOptions
): Promise<UploadResult> => {
  return new Promise<UploadResult>((resolve, reject) => {
    const API_URL = process.env.REACT_APP_API_URL;
    if (!API_URL) {
      reject(new Error("API URL is not configured (REACT_APP_API_URL)."));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    if (opts.companyId) {
      formData.append("company_id", String(opts.companyId));
    }

    const xhr = new XMLHttpRequest();
    let aborted = false;

    opts.onStatus?.("queued");

    xhr.upload.addEventListener("loadstart", () => opts.onStatus?.("uploading"));

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        opts.onProgress?.(pct);
        // When the bytes are fully sent but the server is still working,
        // flip the visible status from "uploading" to "processing".
        if (pct >= 100) opts.onStatus?.("processing");
      }
    });

    xhr.addEventListener("load", () => {
      const json = safeJson(xhr.responseText);
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onStatus?.("done");
        resolve(json ?? { status: true });
      } else {
        opts.onStatus?.("error");
        reject(buildHttpError(xhr.status, json));
      }
    });

    xhr.addEventListener("error", () => {
      if (aborted) return;
      opts.onStatus?.("error");
      reject(new Error("Network error — check your connection and try again."));
    });

    xhr.addEventListener("abort", () => {
      aborted = true;
      opts.onStatus?.("cancelled");
      reject(new DOMException("Upload cancelled", "AbortError"));
    });

    if (opts.signal) {
      if (opts.signal.aborted) {
        opts.onStatus?.("cancelled");
        reject(new DOMException("Upload cancelled", "AbortError"));
        return;
      }
      opts.signal.addEventListener("abort", () => xhr.abort());
    }

    xhr.open("POST", `${API_URL}/media/upload`);
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${localStorage.getItem("token") || ""}`
    );
    xhr.setRequestHeader("Accept", "application/json");
    // Do NOT set Content-Type — the browser sets it with the multipart boundary.

    xhr.send(formData);
  });
};

/* ------------------------------------------------------------------------ */
/* Chunked strategy (architecture only — backend endpoints pending)            */
/* ------------------------------------------------------------------------ */

/**
 * Sketch of the chunked path. To activate this, the backend needs:
 *   POST /api/media/upload/init      { file_name, mime_type, total_size, total_chunks } -> { upload_id }
 *   POST /api/media/upload/chunk     { upload_id, chunk_index } + raw chunk binary
 *   POST /api/media/upload/finalize  { upload_id }                              -> Media JSON
 *
 * Storage during the upload lives under storage/app/uploads/{upload_id}/
 * with one file per chunk; finalize concatenates them, runs the same
 * checksum/dedupe/type-resolution that MediaService::upload performs
 * today, and moves the result into the public signage disk.
 *
 * The chunk size is configurable per-upload but a sensible default
 * is 5 MB — well under any shared-host per-request body limit.
 */
const CHUNK_SIZE_BYTES = 5 * 1024 * 1024;

const uploadChunked = async (
  file: File,
  opts: UploadOptions
): Promise<UploadResult> => {
  // Surface a clear error today and a clear migration path tomorrow.
  // Once the backend endpoints exist, the body below switches to:
  //
  //   const { upload_id } = await initUpload(file);
  //   for (let i = 0; i < totalChunks; i++) {
  //     if (opts.signal?.aborted) throw new DOMException("...", "AbortError");
  //     const blob = file.slice(i * CHUNK_SIZE_BYTES, (i + 1) * CHUNK_SIZE_BYTES);
  //     await sendChunk(upload_id, i, blob, opts);
  //     opts.onProgress?.(Math.round(((i + 1) / totalChunks) * 100));
  //   }
  //   opts.onStatus?.("processing");
  //   return await finalizeUpload(upload_id);
  //
  // Reference the constant so it isn't reported unused.
  void CHUNK_SIZE_BYTES;

  opts.onStatus?.("error");
  return Promise.reject(
    new Error(
      `Files over ${formatBytes(CHUNK_THRESHOLD_BYTES)} require chunked uploads — that backend endpoint isn't deployed yet. Please contact support or upload a smaller file.`
    )
  );
};

/* ------------------------------------------------------------------------ */
/* Helpers                                                                     */
/* ------------------------------------------------------------------------ */

const safeJson = (raw: string): any => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
};

const buildHttpError = (status: number, body: any): Error => {
  if (status === 413) {
    return new Error(
      "File exceeds the server upload limit. Ask your administrator to raise post_max_size / upload_max_filesize."
    );
  }
  if (status === 419) {
    return new Error("Your session expired. Refresh the page and sign in again.");
  }
  if (status === 401 || status === 403) {
    return new Error("You don't have permission to upload media.");
  }
  if (status === 422) {
    const v = body?.errors?.file?.[0] || body?.message;
    return new Error(v || "The uploaded file did not pass validation.");
  }
  if (status >= 500) {
    return new Error(
      body?.message
        ? `Server error: ${body.message}`
        : "Server error while saving the file. Please try again."
    );
  }
  return new Error(
    body?.message || `Upload failed (HTTP ${status}). Please try again.`
  );
};
